/** @format */
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    DegreeProgramme,
    DegreeProgrammeAttainmentApplication,
    LocalizedString,
    Module,
    ModuleAttainmentApplication,
    OtmId,
    Plan,
    StudentWorkflow,
} from 'common-typescript/types';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import {
    AlertsService,
    AlertType,
    AppErrorHandler,
    getConfirmationModalOpener,
    ModuleEntityService,
    PlanEntityService,
    UniversityService,
} from 'sis-components';
import {
    DegreeProgrammeAttainmentApplicationExistsError,
    DegreeProgrammeAttainmentApplicationService,
} from '../../../common/service/degree-programme-attainment-application.service';
import { PlanLoaderService } from '../../../common/service/plan-loader.service';
import { isBachelorsDegree } from '../../../common/utils/graduation-utils';
import { StudentProfile, StudentProfileService } from '../../student-profile.service';

const DEGREE_URNS_WITH_SURVEY_STEP = new Set([
    'urn:code:degree-program-type:bachelors-degree',
    'urn:code:degree-program-type:amk-bachelors-degree',
    'urn:code:degree-program-type:amk-masters-degree',
]);

@Component({
    selector: 'app-graduation-wizard',
    templateUrl: './graduation-wizard.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class GraduationWizardComponent implements OnInit {
    readonly wizardStepKeysAll = ['GRADUATION_WIZARD.STEP_1', 'GRADUATION_WIZARD.STEP_2', 'GRADUATION_WIZARD.STEP_3'];
    readonly wizardStepKeysWithoutSurvey = ['GRADUATION_WIZARD.STEP_1', 'GRADUATION_WIZARD.STEP_3'];

    progressStep = new BehaviorSubject<number>(0);
    step$: Observable<number>;
    graduationOrgId: OtmId;
    degreeProgramme$: Observable<Module | null>;
    validatablePlan$: Observable<ValidatablePlan | null>;
    profile$: Observable<StudentProfile>;
    surveyEnabled$: Observable<boolean>;
    surveyUrl$: Observable<LocalizedString>;
    showSurveyStep$: Observable<boolean>;

    private readonly confirmModalOpener = getConfirmationModalOpener();

    private readonly alertsService = inject(AlertsService);
    private readonly translateService = inject(TranslateService);
    private readonly moduleEntityService = inject(ModuleEntityService);
    private readonly studentProfileService = inject(StudentProfileService);
    private readonly stateService = inject(StateService);
    private readonly uiRouterGlobals = inject(UIRouterGlobals);
    private readonly universityService = inject(UniversityService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly planLoaderService = inject(PlanLoaderService);
    private readonly planEntityService = inject(PlanEntityService);
    private readonly degreeProgrammeAttainmentApplicationService = inject(DegreeProgrammeAttainmentApplicationService);

    ngOnInit(): void {
        const { degreeProgrammeId, planId, step } = this.uiRouterGlobals.params;
        const resolvedPlanAndDegreeProgrammeId$ = (
            planId
                ? of({ planId, degreeProgrammeId })
                : this.planEntityService.getMyPlans().pipe(
                      take(1),
                      switchMap(plans => {
                          const plan = plans
                              .filter(p => p.graduationSurveyOpenedOn)
                              .sort((a, b) => b.graduationSurveyOpenedOn.localeCompare(a.graduationSurveyOpenedOn))[0];
                          if (!plan?.graduationSurveyDegreeProgrammeId) {
                              this.alertsService.addAlert({
                                  type: AlertType.SUCCESS,
                                  message: this.translateService.instant('GRADUATION_WIZARD.NO_PLAN_FOUND'),
                              });
                              this.stateService.go('student.logged-in.profile.applications');
                              return EMPTY;
                          }
                          return of({ planId: plan.id, degreeProgrammeId: plan.graduationSurveyDegreeProgrammeId });
                      }),
                  )
        ).pipe(shareReplay(1));

        // Block entry to the wizard if a graduation request already exists for this degree programme (e.g. one was
        // submitted in another browser tab). Without this, the student could walk through the whole wizard and only
        // hit the "already exists" error on submit. The submit-time guard in create() remains as a last-resort safety
        // net for the race where the request is created after this check but before submission.
        resolvedPlanAndDegreeProgrammeId$
            .pipe(
                switchMap(ids => this.degreeProgrammeAttainmentApplicationService.findActiveApplicationForModule(ids.degreeProgrammeId)),
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe(existingApplication => {
                if (existingApplication) {
                    this.warnApplicationAlreadyExists();
                    this.navigateToWorkflow(existingApplication.id);
                }
            });

        this.degreeProgramme$ = resolvedPlanAndDegreeProgrammeId$.pipe(
            switchMap(ids => this.moduleEntityService.getById(ids.degreeProgrammeId)),
            take(1),
            this.appErrorHandler.defaultErrorHandler(),
            shareReplay(1),
        );

        this.validatablePlan$ = resolvedPlanAndDegreeProgrammeId$.pipe(
            switchMap(ids => this.planLoaderService.createValidatablePlan(ids.planId)),
            this.appErrorHandler.defaultErrorHandler(),
            shareReplay(1),
        );

        this.graduationOrgId = this.universityService.getCurrentUniversityOrgId();

        this.profile$ = this.studentProfileService.profile().pipe(take(1), this.appErrorHandler.defaultErrorHandler());

        this.surveyEnabled$ = this.universityService.getCurrentUniversitySettings().pipe(
            take(1),
            map(settings => settings?.frontendFeatureToggles?.graduationSurveyInWizardEnabled ?? false),
            this.appErrorHandler.defaultErrorHandler(),
        );

        this.surveyUrl$ = this.degreeProgramme$.pipe(
            switchMap(dp =>
                this.universityService.getCurrentUniversitySettings().pipe(
                    take(1),
                    map(settings =>
                        isBachelorsDegree(dp as DegreeProgramme) ? settings?.bachelorsGraduateSurveyUrl : settings?.amkGraduateSurveyUrl,
                    ),
                ),
            ),
            this.appErrorHandler.defaultErrorHandler(),
        );

        this.showSurveyStep$ = combineLatest([this.degreeProgramme$, this.profile$]).pipe(
            map(
                ([dp, profile]) =>
                    DEGREE_URNS_WITH_SURVEY_STEP.has((dp as DegreeProgramme)?.degreeProgramTypeUrn) &&
                    !profile?.graduationSurveyExemptionGranted,
            ),
        );

        const defaultStep = planId ? 0 : this.wizardStepKeysAll.length - 1;
        const parsed = step !== undefined ? parseInt(step, 10) : defaultStep;
        const initialStep = Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(this.wizardStepKeysAll.length - 1, parsed));
        this.progressStep = new BehaviorSubject<number>(initialStep);

        this.step$ = combineLatest([this.progressStep, this.showSurveyStep$.pipe(startWith(null as boolean | null))]).pipe(
            map(([s, showSurvey]) => {
                if (showSurvey === null) return s;
                const max = showSurvey ? this.wizardStepKeysAll.length - 1 : this.wizardStepKeysWithoutSurvey.length - 1;
                return Math.min(s, max);
            }),
            distinctUntilChanged(),
        );
    }

    next(): void {
        this.progressStep.next(Math.min(this.wizardStepKeysAll.length - 1, this.progressStep.value + 1));
    }

    openGraduationSurvey(): void {
        this.validatablePlan$
            .pipe(
                take(1),
                switchMap(validatablePlan => {
                    if (!validatablePlan?.plan) {
                        return of(null);
                    }
                    const updatedPlan = {
                        ...validatablePlan.plan,
                        graduationSurveyOpenedOn: new Date().toISOString(),
                        graduationSurveyDegreeProgrammeId: this.uiRouterGlobals.params.degreeProgrammeId ?? null,
                    };
                    return this.planEntityService.updateMyPlan(updatedPlan);
                }),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();
    }

    previous(): void {
        this.progressStep.next(Math.max(0, this.progressStep.value - 1));
    }

    submitApplication(formData: Partial<DegreeProgrammeAttainmentApplication>): void {
        const modalRef = this.confirmModalOpener({
            title: 'GRADUATION_WIZARD.CONFIRM_DIALOG.TITLE',
            description: 'GRADUATION_WIZARD.CONFIRM_DIALOG.DESCRIPTION',
            confirmText: 'GRADUATION_WIZARD.CONFIRM_DIALOG.CONFIRM',
            cancelText: 'GRADUATION_WIZARD.CONFIRM_DIALOG.CANCEL',
        });

        modalRef.result
            .then(() => this.doSubmit(formData))
            .catch(() => {
                // Cancelled by user.
            });
    }

    private doSubmit(formData: Partial<DegreeProgrammeAttainmentApplication>): void {
        combineLatest([this.degreeProgramme$, this.validatablePlan$])
            .pipe(
                take(1),
                switchMap(([degreeProgramme, validatablePlan]) => {
                    if (!degreeProgramme || !validatablePlan) {
                        return of(null);
                    }
                    const planValidationResult = PlanValidationTs.validatePlan(validatablePlan);
                    return this.degreeProgrammeAttainmentApplicationService
                        .create(formData, degreeProgramme, validatablePlan.studyRight, validatablePlan, planValidationResult)
                        .pipe(map(results => ({ results, validatablePlan })));
                }),
                catchError(error => {
                    // The student may have reached the wizard from a stale view (e.g. an already-submitted request that
                    // was not yet reflected in the plan tree) where the "apply" action was still shown. Instead of
                    // surfacing a raw error, notify them and route them to the existing graduation request.
                    if (error instanceof DegreeProgrammeAttainmentApplicationExistsError) {
                        this.warnApplicationAlreadyExists();
                        this.navigateToWorkflow(error.existingApplicationId);
                        return of(null);
                    }
                    return throwError(() => error);
                }),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe(data => {
                if (!data) {
                    return;
                }
                const { results, validatablePlan } = data;
                this.alertsService.addAlert({
                    type: AlertType.SUCCESS,
                    message: this.translateService.instant('STUDENT_APPLICATIONS.DEGREE_PROGRAMME_ATTAINMENT_APPLICATION.CREATED'),
                });
                if (validatablePlan.plan) {
                    const updatedPlan = {
                        ...validatablePlan.plan,
                        graduationSurveyOpenedOn: null,
                        graduationSurveyDegreeProgrammeId: null,
                    } as Plan;
                    this.planEntityService.updateMyPlan(updatedPlan).pipe(take(1), this.appErrorHandler.defaultErrorHandler()).subscribe();
                }
                const workflow = results.find(
                    (r: ModuleAttainmentApplication | StudentWorkflow | undefined) =>
                        r && (r as StudentWorkflow).type === 'DegreeProgrammeAttainmentWorkflow',
                ) as StudentWorkflow;
                if (workflow?.id) {
                    this.navigateToWorkflow(workflow.id);
                }
            });
    }

    private warnApplicationAlreadyExists(): void {
        this.alertsService.addAlert({
            type: AlertType.WARNING,
            message: this.translateService.instant('GRADUATION_WIZARD.ALREADY_EXISTS'),
        });
    }

    private navigateToWorkflow(workflowId: OtmId): void {
        this.stateService.go('student.logged-in.profile.applications.degree-programme-attainment-workflow', { applicationId: workflowId });
    }
}
